/**
 * Common
 */

export interface ApiSuccess<T> {
  code: string;
  data: T;
}

export interface ApiError {
  code: string;
  message: string;
}

export interface ErrorInfo {
  name: string;
  message: string;
}

export enum TorrentStatus {
  PENDING = "PENDING",
  PUSHED = "PUSHED",
  CONFLICTED = "CONFLICTED",
  ERROR = "ERROR",
  ARCHIVING = "ARCHIVING",
  ARCHIVED = "ARCHIVED"
}

export interface TorrentFile {
  bytesCompleted: number;
  priority: number;
  wanted: boolean;
  length: number;
  name: string;
  filename: string;
  indice: number;
}

export interface TorrentFolder {
  name: string;
  children: TorrentFile[];
  wanted: "all" | "partial" | "none";
}

export type TorrentNode = TorrentFile | TorrentFolder;

export interface Tracker {
  announce: string;
  id: number;
  scrape: string;
  tier: number;
}

export function isTorrentFolder(arg: any): arg is TorrentFolder {
  return !!arg.children;
}

export interface TorrentStat {
  state: string;
  progress: number;
  uploadRatio: number;
  activityDate: number;
  uploadSpeed: number;
  downloadSpeed: number;
}

export interface TorrentStats {
  [key: string]: TorrentStat;
}

export interface TorrentStatsDiff {
  updated: TorrentStats;
  removed: string[];
}

export interface Torrent extends Partial<TorrentStat> {
  id: number;
  title: string;
  name: string;
  season?: number;
  episode?: number;
  status: TorrentStatus;
  folder: string;
  message?: string;
  archiveSrc?: string;
  archiveDest?: string;
  size?: number;
  hash: string;
  subtitleDir?: string;
  subtitles?: Array<string>;
  data?: Buffer;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  files?: TorrentFile[];
  trackers?: Tracker[];
  nodes?: TorrentNode[];
  pending?: boolean;
  downloadError?: string;
}

export interface TorrentStatDiff {
  updated: any;
  removed: string[];
}

export interface DownloadStat {
  uploadSpeed: number;
  downloadSpeed: number;
}

export interface Stat {
  dlStat: StatState;
  trStats: TorrentStats;
}

/**
 * Setting
 */

export interface SettingState {
  pending: boolean;
  folder: string;
  archiveFolder: string;
}

export const SET_SETTING_PENDING = "SET_SETTING_PENDING";
export interface SetSettingPendingAction {
  type: typeof SET_SETTING_PENDING;
  pending: boolean;
}

export interface Settings {
  folder: string;
  archiveFolder: string;
}

export const SET_SETTINGS = "SET_SETTINGS";
export interface SetSettingsAction {
  type: typeof SET_SETTINGS;
  settings: Settings;
}

export type SettingActions = SetSettingsAction | SetSettingPendingAction;

/**
 * General purpose notification
 */

export type ToastVariant =
  | "success"
  | "error"
  | "warning"
  | "default"
  | undefined;

export interface ToastState {
  message?: string;
  variant?: ToastVariant;
}

export const SET_TOAST_MSG = "SET_NOTIFICATION_MSG";
export interface SetToastMsgAction {
  type: typeof SET_TOAST_MSG;
  message?: string;
  variant?: ToastVariant;
}

export type ToastActions = SetToastMsgAction;

/**
 * Search
 */
export interface SearchState {
  keyword?: string;
  status?: string;
}

export const UPDATE_SEARCH_KEYWORD = "UPDATE_SEARCH_KEYWORD";
export interface UpdateSearchKeywordAction {
  type: typeof UPDATE_SEARCH_KEYWORD;
  keyword?: string;
}

export const UPDATE_SEARCH_STATUS = "UPDATE_SEARCH_STATUS";
export interface UpdateSearchStatusAction {
  type: typeof UPDATE_SEARCH_STATUS;
  status?: TorrentStatus;
}

export type UpdateSearchActions =
  | UpdateSearchKeywordAction
  | UpdateSearchStatusAction;

/**
 * Torrent
 */

export interface TorrentState {
  pending: boolean;
  list: Torrent[];
  count: number;
  error?: ErrorInfo;
  active?: Torrent;
  selected: number[];
  size: number;
}

export const FETCH_TORRENTS_PENDING = "FETCH_TORRENTS_PENDING";
export interface FetchTorrentPendingAction {
  type: typeof FETCH_TORRENTS_PENDING;
}

export const FETCH_TORRENTS_SUCCESS = "FETCH_TORRENTS_SUCCESS";
export interface FetchTorrentSuccessAction {
  type: typeof FETCH_TORRENTS_SUCCESS;
  torrents: Torrent[];
  append?: boolean;
}

export const FETCH_TORRENTS_ERROR = "FETCH_TORRENTS_ERROR";
export interface FetchTorrentErrorAction {
  type: typeof FETCH_TORRENTS_ERROR;
  error: ErrorInfo;
}

export const SET_TORRENTS_COUNT = "SET_TORRENTS_COUNT";
export interface SetTorrentsCountAction {
  type: typeof SET_TORRENTS_COUNT;
  count: number;
}

export const SET_ACTIVE_TORRENT = "SET_ACTIVE_TORRENT";
export interface SetActiveTorrentAction {
  type: typeof SET_ACTIVE_TORRENT;
  torrent?: Torrent;
}

export const SET_ACTIVE_TORRENT_FILES = "SET_ACTIVE_TORRENT_FILES";
export interface SetActiveTorrentFilesAction {
  type: typeof SET_ACTIVE_TORRENT_FILES;
  files: TorrentFile[];
  nodes: TorrentNode[];
  id: number;
}

export const SET_ACTIVE_TORRENT_TRACKERS = "SET_ACTIVE_TORRENT_TRACKERS";
export interface SetActiveTorrentTrackersAction {
  type: typeof SET_ACTIVE_TORRENT_TRACKERS;
  trackers: Tracker[];
  id: number;
}

export const SET_SELECTED_TORRENTS = "SET_SELECTED_TORRENTS";
export interface SetSelectedTorrents {
  type: typeof SET_SELECTED_TORRENTS;
  torrentIds: number[];
}

export const TOGGLE_TORRENT_PENDING = "TOGGLE_TORRENT_PENDING";
export interface ToggleTorrentPendingAction {
  type: typeof TOGGLE_TORRENT_PENDING;
  torrentId: number;
  pending: boolean;
}

export const TOGGLE_TORRENT_SUBTITLING_STATUS =
  "TOGGLE_TORRENT_SUBTITLING_STATUS";
export interface ToggleTorrentSubtitlingAction {
  type: typeof TOGGLE_TORRENT_SUBTITLING_STATUS;
  torrentId: number;
  subtitling: boolean;
}

export const SYNC_TORRENT_STATS = "SYNC_TORRENT_STATS";
export interface SyncTorrentStatsAction {
  type: typeof SYNC_TORRENT_STATS;
  diff: TorrentStatsDiff;
}

export type TorrentActions =
  | FetchTorrentPendingAction
  | FetchTorrentSuccessAction
  | FetchTorrentErrorAction
  | SetTorrentsCountAction
  | SetActiveTorrentAction
  | SetActiveTorrentFilesAction
  | SetActiveTorrentTrackersAction
  | SetSelectedTorrents
  | ToggleTorrentPendingAction
  | SyncTorrentStatsAction;

/**
 * Adding torrent
 */

export interface AddingState {
  show: boolean;
  pending: boolean;
  added?: Partial<Torrent>;
  error?: ErrorInfo;
}

export enum AddingDataType {
  FILE = "File",
  URL = "URL"
}

export interface AddingForm {
  type: string;
  data: string;
  folder: string;
}

export const TOGGLE_ADDING_DIALOG = "TOGGLE_ADDING_DIALOG";
export interface ToggleAddingDialogAction {
  type: typeof TOGGLE_ADDING_DIALOG;
  show: boolean;
}

export const TOGGLE_ADDING_PENDING = "TOGGLE_ADDING_PENDING";
export interface ToggleAddingPendingAction {
  type: typeof TOGGLE_ADDING_PENDING;
  pending: boolean;
}

export const ADD_TORRENT_SUCCESS = "ADD_TORRENT_SUCCESS";
export interface AddTorrentSuccess {
  type: typeof ADD_TORRENT_SUCCESS;
  added: Partial<Torrent>;
}

export const ADD_TORRENT_ERROR = "ADD_TORRENT_ERROR";
export interface AddTorrentError {
  type: typeof ADD_TORRENT_ERROR;
  error: ErrorInfo;
}

export const RESET_ADDING_STATE = "RESET_ADDING_STATE";
export interface ResetAddingState {
  type: typeof RESET_ADDING_STATE;
}

export type AddingDialogActions =
  | ToggleAddingDialogAction
  | ToggleAddingPendingAction
  | AddTorrentSuccess
  | AddTorrentError
  | ResetAddingState;

/**
 * Deleting Torrents
 */

export interface DeletingState {
  pending: boolean;
}

export const TOGGLE_DELETING_PENDING = "TOGGLE_DELETING_PENDING";
export interface ToggleDeletingPendingAction {
  type: typeof TOGGLE_DELETING_PENDING;
  pending?: boolean;
}

export type DeletingActions = ToggleDeletingPendingAction;

/**
 * Archiving Torrents
 */
export interface QueueItem {
  id: number;
  title: string;
  name: string;
  archiveSrc: string;
  archiveDest: string;
  size: number;
  isCurrent?: boolean;
}

export interface QueueChangeData {
  current: QueueItem;
  queue: QueueItem[];
}

export interface ArchiveState {
  pending: boolean;
  queue: QueueItem[];
}

export const TOGGLE_ARCHIVE_PENDING = "TOGGLE_ARCHIVE_PENDING";
export interface ToggleArchivePendingAction {
  type: typeof TOGGLE_ARCHIVE_PENDING;
  pending: boolean;
}

export const SET_ARCHIVE_QUEUE = "SET_ARCHIVE_QUEUE";
export interface SetArchiveQueueAction {
  type: typeof SET_ARCHIVE_QUEUE;
  queue: QueueItem[];
}

export type ArchiveActions = ToggleArchivePendingAction | SetArchiveQueueAction;

/**
 * Stat
 */
export type StatState = DownloadStat;

export const SYNC_STATS = "SYNC_STATS";
export interface SyncStatAction {
  type: typeof SYNC_STATS;
  stat: DownloadStat;
}

export type StatActions = SyncStatAction;


/**
 * Login
 */
export const SET_LOGIN = "SET_LOGIN";
export interface SetLoginAction {
  type: typeof SET_LOGIN;
  login: boolean;
}

export type LoginActions = SetLoginAction;